import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { API_URL, api } from "../api/client";
import { onApiUpdated } from "../api/updates";
import HttpError from "../types/HttpError";
import { Topic, useChanged } from "./changes";
import { takePrefetched } from "./prefetch";
import { queryClient } from "./queryClient";

export { forgetApiResponses } from "./queryClient";

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  // What the API said when it refused (a "moved" 404's `workId`, ADR
  // 0030); null while loading, after a success, and for a failed network.
  errorBody: HttpError | null;
  reload: () => void;
  mutate: Dispatch<SetStateAction<T | null>>;
}

const keyOf = (auth: boolean, path: string | null) => ["api", auth, path];

// Paths whose stored copy the service worker has just made the API's.
const updated = new Set<string>();

// `path` once it has stayed put for `delay` ms (a search as you type); no
// path, or no delay, at once.
const useSettled = (path: string | null, delay: number): string | null => {
  const [settled, setSettled] = useState(delay > 0 ? null : path);
  useEffect(() => {
    if (delay === 0) return;
    const handle = setTimeout(() => setSettled(path), delay);
    return () => clearTimeout(handle);
  }, [path, delay]);
  return delay > 0 && path !== null ? settled : path;
};

// A GET's answer for a page, through TanStack Query's cache (ADR 0055).
export function useApi<T>(
  path: string | null,
  // `about`: what of yours it shows, so a change to it reloads it.
  options?: { auth?: boolean; debounce?: number; about?: Topic[] },
): UseApiResult<T> {
  const auth = options?.auth ?? true;
  const asked = useSettled(path, options?.debounce ?? 0);
  const id = `${auth}:${asked}`;

  const query = useQuery<T>(
    {
      queryKey: keyOf(auth, asked),
      queryFn: () => {
        const key = keyOf(auth, asked);
        // With nothing of this path's on screen, the device's stored copy
        // shows at once, so a poor connection doesn't leave the page empty
        // while it hangs; the worker fetches behind it and says if the
        // API's answer differs. With its data showing (a reload after an
        // edit, a page that comes back), it asks the API, unless the
        // worker has just stored its answer.
        const stored =
          updated.delete(id) || queryClient.getQueryData(key) === undefined;
        return (
          (auth && takePrefetched<T>(asked as string)) ||
          api.get<T>(asked as string, auth, stored)
        );
      },
      enabled: asked !== null,
      // A new path keeps the last one's data showing while it loads, as
      // pages expect (they show it under the loading state).
      placeholderData: keepPreviousData,
    },
    queryClient,
  );

  // Reloads asked for together (a failed save's, and the change it says)
  // are one fetch; one asked for later starts afresh.
  const reloading = useRef(false);
  const reload = useCallback(() => {
    if (reloading.current) return;
    reloading.current = true;
    void Promise.resolve().then(() => {
      reloading.current = false;
      return queryClient.refetchQueries({
        queryKey: keyOf(auth, asked),
        exact: true,
      });
    });
  }, [auth, asked]);
  useChanged(options?.about, reload);

  useEffect(() => {
    if (asked === null) return;
    return onApiUpdated((changed) => {
      if (changed !== new URL(`${API_URL}${asked}`).href) return;
      updated.add(id);
      reload();
    });
  }, [asked, id, reload]);

  // `mutate(null)` empties the page without forgetting what it had: the
  // answer it cleared stays the one it comes back to. It holds until the
  // next answer, or edit, stored.
  const [clearedAt, setClearedAt] = useState<number | null>(null);
  const cleared = clearedAt === query.dataUpdatedAt;

  // A page's own edits (an optimistic reorder) are what it should come back
  // to, too.
  const mutate: Dispatch<SetStateAction<T | null>> = useCallback(
    (next) => {
      const key = keyOf(auth, asked);
      const current = cleared
        ? null
        : (queryClient.getQueryData<T>(key) ?? null);
      const value =
        typeof next === "function"
          ? (next as (prev: T | null) => T | null)(current)
          : next;
      if (value === null) {
        setClearedAt(query.dataUpdatedAt);
        return;
      }
      setClearedAt(null);
      queryClient.setQueryData(key, value);
    },
    [auth, asked, cleared, query.dataUpdatedAt],
  );

  if (path === null) {
    return {
      data: null,
      loading: false,
      error: false,
      errorBody: null,
      reload,
      mutate,
    };
  }

  // A new path is loading from its first render, through its debounce, and
  // while the last path's data stands in for it. A retry of one that failed
  // with nothing showing loads too; a reload of one showing its data
  // refreshes in the background.
  const waiting = asked !== path;
  const data = cleared ? null : (query.data ?? null);
  const failed = query.isError && !query.isFetching && !waiting;
  return {
    data,
    loading:
      waiting ||
      query.isPending ||
      query.isPlaceholderData ||
      (query.isFetching && query.data === undefined),
    error: failed,
    errorBody: failed
      ? ((query.error as { body?: HttpError | null }).body ?? null)
      : null,
    reload,
    mutate,
  };
}
